import { useEffect } from "react";
import Nav from "@/components/aix/Nav";
import { Footer } from "@/components/aix/Sections2";
import { ArrowRight } from "lucide-react";

const JOE_BIO = [
  "Joe Beccalori is a marketing innovator, entrepreneur, and digital strategist with over 30 years of experience combining marketing expertise with emerging technologies to help businesses grow.",
  "As Founder and CEO of Interact Marketing, established in 2007, Joe has spent nearly two decades advancing digital marketing strategies and solutions, helping organizations navigate the evolution of online advertising, search engine optimization, marketing automation, and artificial intelligence.",
  "Joe is also the inventor of the Web Marketing Machine™, an integrated marketing technology system developed through Interact Marketing that brings together search engine optimization (SEO), generative engine optimization (GEO), paid advertising, retargeting, intelligent automation, lead management, and performance analytics into a unified business growth solution.",
  "The Web Marketing Machine represents Joe's vision of transforming traditional marketing services into scalable, technology-powered solutions that combine marketing strategy, software, automation, AI, and human expertise.",
  "A published industry contributor, speaker, and longtime advocate of entrepreneurship and professional collaboration, Joe has consistently focused on identifying emerging technologies and finding innovative ways to apply them to real-world marketing and business challenges.",
  "Recognizing the extraordinary pace of AI development and the value of shared expertise, Joe founded AI Advantage Exchange (AIX), an invitation-only mastermind bringing together accomplished software developers, AI innovators, entrepreneurs, and experienced technology practitioners.",
  "The group's purpose is to exchange knowledge, explore emerging technologies, share real-world projects and discoveries, and create opportunities for collaboration among people actively building and applying AI solutions.",
  "Joe believes that the next generation of breakthroughs will emerge not just from individual innovation, but from the exchange of ideas among talented people with diverse experiences and complementary expertise.",
];

const CREDENTIALS = [
  "Founder & CEO, Interact Marketing — est. 2007",
  "Inventor, Web Marketing Machine™",
  "30+ years in marketing & emerging tech",
  "Published contributor & speaker",
];

export default function Founders() {
  useEffect(() => {
    document.title = "Founding Members — AI Advantage Exchange";
    return () => {
      document.title = "AI Advantage Exchange — Curated AI Professional Community";
    };
  }, []);

  return (
    <div
      className="bg-white text-slate-900 min-h-screen selection:bg-aix-cyan selection:text-black antialiased"
      data-testid="founders-page"
    >
      <Nav />
      <main>
        {/* PAGE HERO IMAGE + HEADER */}
        <section className="relative overflow-hidden" data-testid="founders-header">
          <img
            src="https://images.unsplash.com/photo-1587825140708-dfaf72ae4b04?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1Mjh8MHwxfHNlYXJjaHwxfHx0ZWNoJTIwY29uZmVyZW5jZSUyMHByZXNlbnRhdGlvbnxlbnwwfHx8fDE3OTIwMTI4MTB8MA&ixlib=rb-4.1.0&q=85"
            alt="Technology leaders gathered at a keynote presentation"
            className="absolute inset-0 w-full h-full object-cover"
            data-testid="founders-hero-image"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#071C3E]/90 via-[#0E2A5C]/70 to-[#071C3E]/40"></div>
          <div className="max-w-6xl mx-auto px-6 relative z-10 pt-24 pb-16 md:pt-32 md:pb-20">
            <div className="font-mono-lbl text-xs text-aix-cyan tracking-[0.2em] uppercase mb-6 flex items-center gap-2">
              <span className="inline-block w-2 h-2 rounded-full bg-aix-cyan shadow-[0_0_8px_#2AAEE4]"></span>
              FOUNDING MEMBERS
            </div>
            <h1 className="font-display text-[clamp(38px,5vw,64px)] font-black tracking-tight leading-[1.05] text-white max-w-3xl" data-testid="founders-headline">
              Connecting the minds{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-white via-[#7ED4F7] to-[#2AAEE4]">
                building what's next.
              </span>
            </h1>
          </div>
        </section>

        {/* FOUNDER PROFILE */}
        <section className="py-20 md:py-28 max-w-6xl mx-auto px-6" data-testid="founder-card-joe">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-4">
              <div className="rounded-2xl overflow-hidden border border-slate-200 shadow-xl">
                <img
                  src="/assets/joe-beccalori.jpg"
                  alt="Joe Beccalori, Founder of AI Advantage Exchange"
                  className="w-full h-auto object-cover"
                  data-testid="founder-photo-joe"
                />
              </div>
              <div className="mt-6 flex flex-wrap gap-2">
                {CREDENTIALS.map((c) => (
                  <span
                    key={c}
                    className="font-mono-lbl text-[10px] text-aix-cyandeep uppercase tracking-wider bg-sky-50 px-2.5 py-1 rounded border border-aix-cyan/30"
                  >
                    {c}
                  </span>
                ))}
              </div>
            </div>

            <div className="lg:col-span-8">
              <span className="font-mono-lbl text-xs text-aix-cyandeep tracking-widest uppercase">
                Founder, AI Advantage Exchange (AIX)
              </span>
              <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight text-slate-900 mt-2" data-testid="founder-name-joe">
                Joe Beccalori
              </h2>
              <p className="text-sm md:text-base text-slate-500 mt-3 font-medium">
                Marketing Innovator | Founder &amp; CEO, Interact Marketing | Technology Entrepreneur | AI Strategist
              </p>
              <div className="mt-8 space-y-5">
                {JOE_BIO.map((paragraph, i) => (
                  <p key={i} className="text-slate-600 text-base leading-relaxed">
                    {paragraph}
                  </p>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="pb-24 max-w-6xl mx-auto px-6">
          <div className="border border-slate-200 bg-slate-50 rounded-2xl p-8 md:p-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-6" data-testid="founders-cta">
            <div>
              <h3 className="font-display text-2xl font-bold text-slate-900">Join the founding class</h3>
              <p className="text-slate-500 text-sm mt-2 max-w-lg">
                AIX is invitation-only. Tell us what you are shipping with AI — what you are building matters more than your title.
              </p>
            </div>
            <a
              href="/#apply"
              className="inline-flex items-center gap-2 bg-aix-navy hover:bg-[#16407F] text-white text-sm font-semibold px-6 py-3.5 rounded transition-colors shadow-[0_0_20px_rgba(42,174,228,0.25)] whitespace-nowrap"
              data-testid="founders-apply-cta"
            >
              Join Waitlist <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
